import { useState, useEffect, type FormEvent } from 'react';
import { signInWithEmailAndPassword, signInWithCustomToken } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '@/shared/firebase/config';
import { useAuth } from '@/shared/auth/AuthContext';
import { fetchAccessCode, type AccessCode } from '@/shared/firebase/codes';
import { isAdminUser } from '@/shared/firebase/admin';

type Mode = 'code' | 'email';

export default function LoginPage() {
  const { user, accessCode, setAccessCode, codeInvalid } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const intendedFrom = (location.state as { from?: string } | null)?.from;
  const intendedRole = intendedFrom?.startsWith('/') ? intendedFrom.slice(1).split('/')[0] : null;

  const initialMode: Mode = intendedRole === 'admin' ? 'email' : 'code';
  const [mode, setMode] = useState<Mode>(initialMode);

  // Prioridad: admin > código > usuario regular.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (await isAdminUser(user)) {
        if (cancelled) return;
        navigate('/admin', { replace: true });
        return;
      }
      if (cancelled) return;
      if (accessCode) {
        navigate(`/${accessCode.role}`, { replace: true });
        return;
      }
      if (user) {
        if (intendedFrom && intendedFrom !== '/login' && intendedFrom !== '/') {
          navigate(intendedFrom, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user, accessCode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-full grid place-items-center px-4 bg-gradient-to-br from-brand-50 via-white to-success-50">
      <div className="w-full max-w-md card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-brand text-white grid place-items-center font-extrabold text-lg shadow-soft">GC</div>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900">Geocuidado</h1>
            <p className="text-xs text-slate-500">
              {intendedRole
                ? <>Acceso a <b className="capitalize">{intendedRole}</b></>
                : 'Localización y cuidado de residentes'}
            </p>
          </div>
        </div>

        {codeInvalid && (
          <div className="rounded-xl bg-warning-50 text-warning px-3 py-2 text-sm mb-4">
            Tu código de acceso ha sido revocado o ya no es válido. Pídele al administrador uno nuevo.
          </div>
        )}

        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5">
          <ModeBtn active={mode==='code'}  onClick={()=>setMode('code')}>Código</ModeBtn>
          <ModeBtn active={mode==='email'} onClick={()=>setMode('email')}>Admin</ModeBtn>
        </div>

        {mode === 'code' ? <CodeForm onRedeem={setAccessCode} /> : <EmailForm onBeforeLogin={() => setAccessCode(null)} />}

        {mode === 'email' && <FirstAccessPanel />}

        <p className="text-center text-xs text-slate-400 mt-6">v0.4 · Geocuidado</p>
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-lg text-sm font-semibold transition
        ${active ? 'bg-white text-brand shadow-soft' : 'text-slate-500 hover:text-slate-700'}`}
    >{children}</button>
  );
}

function CodeForm({ onRedeem }: { onRedeem: (c: AccessCode) => void }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const c = await fetchAccessCode(code);
      if (!c) { setError('Código no válido o revocado.'); return; }
      onRedeem(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label" htmlFor="code">Código de acceso</label>
        <input
          id="code" required autoFocus inputMode="text" autoCapitalize="characters"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          maxLength={8}
          className="input text-center text-2xl tracking-[0.3em] font-bold uppercase"
          placeholder="XXXXXXX"
        />
        <p className="text-xs text-slate-500 mt-2">Pídeselo al administrador de la residencia.</p>
      </div>
      {error && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{error}</div>}
      <button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full disabled:opacity-50">
        {loading ? 'Verificando…' : 'Entrar'}
      </button>
    </form>
  );
}

function FirstAccessPanel() {
  const [showFirstAccess, setShowFirstAccess] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const slug = hostname.includes('-') ? window.location.pathname.split('/')[1] || '' : '';
      const resp = await fetch(
        'https://europe-west1-apps-en-desarrollo.cloudfunctions.net/firstTimeTenantLogin',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: { slug, email: email.trim(), code: code.trim().toUpperCase(), appId: 'geocuidado' } }),
        },
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`No se pudo recuperar el acceso (${resp.status}). ${txt}`);
      }
      const json = await resp.json();
      const customToken: string | undefined = json?.result?.customToken;
      if (!customToken) throw new Error('Respuesta inválida del servidor.');
      await signInWithCustomToken(auth, customToken);
      setOkMsg('Acceso concedido. Redirigiendo…');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error solicitando acceso');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mt-4 text-center">
      <a
        onClick={() => setShowFirstAccess((v) => !v)}
        style={{ fontSize: 11, opacity: 0.6, textDecoration: 'underline', cursor: 'pointer' }}
      >
        ¿No has recibido tu contraseña?
      </a>
      {showFirstAccess && (
        <form onSubmit={submit} className="space-y-3 mt-3 text-left">
          <div>
            <label className="label" htmlFor="fa-email">Correo</label>
            <input
              id="fa-email" type="email" required
              className="input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@residencia.com"
            />
          </div>
          <div>
            <label className="label" htmlFor="fa-code">Código de empresa</label>
            <input
              id="fa-code" required
              className="input uppercase"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              maxLength={8} minLength={6}
              placeholder="ABCDE12"
            />
          </div>
          {error && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{error}</div>}
          {okMsg && <div className="rounded-xl bg-success-50 text-success px-3 py-2 text-sm">{okMsg}</div>}
          <button type="submit" disabled={loading || !email || code.length < 6} className="btn-primary w-full disabled:opacity-50">
            {loading ? 'Solicitando…' : 'Solicitar acceso'}
          </button>
        </form>
      )}
    </div>
  );
}

function EmailForm({ onBeforeLogin }: { onBeforeLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      // Limpia código previo si hubiera — admin tiene su propio camino.
      onBeforeLogin();
      await signInWithEmailAndPassword(auth, email.trim(), pass);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de acceso');
    } finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label" htmlFor="email">Correo</label>
        <input
          id="email" type="email" required autoComplete="email"
          className="input" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="usuario@residencia.com"
        />
      </div>
      <div>
        <label className="label" htmlFor="pass">Contraseña</label>
        <input
          id="pass" type="password" required autoComplete="current-password"
          className="input" value={pass} onChange={(e) => setPass(e.target.value)}
        />
      </div>
      {error && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{error}</div>}
      <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
        {loading ? 'Entrando…' : 'Entrar'}
      </button>
    </form>
  );
}
